import { StoreService } from '@hb/services/store/store.service';
import { PropertyBuilder } from '@hb/builder';
import { AccordionStep, FieldSubject, PropertyOption } from '@hb/domain';
import { AbstractStoreStepInject } from '@hb/services/store/abstract-store-step-inject.service';
import { AccordionStateService } from '@hb/services/accordion-state/accordion-state.service';

import { inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, skip } from 'rxjs';

import { EnergyLabel, Property, PropertyType } from '@ap/ap-interface-apix-hypotheken-berekening';
import { Money } from '@ap/interface-bspc-core';

@Injectable({
    providedIn: 'root'
})
export class PropertyStoreService extends AbstractStoreStepInject implements OnDestroy {
    protected readonly storeService = inject(StoreService);
    protected accordionStateService = inject(AccordionStateService);

    private readonly propertySubject: BehaviorSubject<Property>;

    private readonly propertyTypeSubject: BehaviorSubject<PropertyType>;
    private readonly energyLabelSubject: FieldSubject<EnergyLabel>;
    private readonly purchasePriceSubject: FieldSubject<Money>;
    private readonly sustainabilityLoanSubject: FieldSubject<boolean>;
    private readonly sustainabilitySubject: FieldSubject<Money>;
    private readonly renovationSubject: FieldSubject<boolean>;
    private readonly renovationAmountSubject: FieldSubject<Money>;

    constructor() {
        super();
        this.summary = PropertyOption.SUMMARY;
        this.activeOptionSubject = new BehaviorSubject(
            this.accordionStateService.getStepIsFinished(AccordionStep.PROPERTY) ? PropertyOption.SUMMARY : PropertyOption.PROPERTY_TYPE
        );

        const propertyState = this.getPropertyState();

        this.propertySubject = new BehaviorSubject<Property>(propertyState);
        this.propertyTypeSubject = new BehaviorSubject<PropertyType>(propertyState?.property_type);
        this.energyLabelSubject = new FieldSubject<EnergyLabel>(propertyState?.energy_label);
        this.purchasePriceSubject = new FieldSubject<Money>(propertyState?.purchase_price);
        this.sustainabilityLoanSubject = new FieldSubject<boolean>(propertyState?.sustainability_loan);
        this.sustainabilitySubject = new FieldSubject<Money>(propertyState?.sustainability_amount);
        this.renovationSubject = new FieldSubject<boolean>(propertyState?.renovation);
        this.renovationAmountSubject = new FieldSubject<Money>(propertyState?.renovation_amount);

        this.setUp();
    }

    setUp(): void {
        this.getProperty()
            .pipe(skip(1))
            .subscribe((property: Property) => this.storeService.setProperty(property));
        this.getPropertyType()
            .pipe(skip(1))
            .subscribe(() => this.update());
        this.getEnergyLabel().subscribe(() => this.update());
        this.getPurchasePrice().subscribe(() => this.update());
        this.getSustainabilityLoan().subscribe(() => this.update());
        this.getSustainability().subscribe(() => this.update());
        this.getRenovation().subscribe(() => this.update());
        this.getRenovationAmount().subscribe(() => this.update());
    }

    getPropertyState(): Property {
        return this.storeService.getState()?.property;
    }

    getProperty(): Observable<Property> {
        return this.propertySubject.asObservable();
    }

    getPropertyType(): Observable<PropertyType> {
        return this.propertyTypeSubject.asObservable();
    }

    getEnergyLabel(): Observable<EnergyLabel> {
        return this.energyLabelSubject.asObservable();
    }

    getPurchasePrice(): Observable<Money> {
        return this.purchasePriceSubject.asObservable();
    }

    getSustainabilityLoan(): Observable<boolean> {
        return this.sustainabilityLoanSubject.asObservable();
    }

    getSustainability(): Observable<Money> {
        return this.sustainabilitySubject.asObservable();
    }

    getRenovation(): Observable<boolean> {
        return this.renovationSubject.asObservable();
    }

    getRenovationAmount(): Observable<Money> {
        return this.renovationAmountSubject.asObservable();
    }

    setPropertyType(propertyType: PropertyType): void {
        if (propertyType !== this.propertyTypeSubject.value) {
            this.energyLabelSubject.next(null);
        }
        if (propertyType === PropertyType.NONE) {
            this.purchasePriceSubject.next(null);

            this.sustainabilityLoanSubject.next(null);
            this.sustainabilitySubject.next(null);

            this.renovationSubject.next(null);
            this.renovationAmountSubject.next(null);
        }

        this.propertyTypeSubject.next(propertyType);
    }

    setEnergyLabel(energyLabel: EnergyLabel): void {
        this.energyLabelSubject.next(energyLabel);
    }

    setPurchasePrice(purchasePrice: Money): void {
        this.purchasePriceSubject.next(purchasePrice);
    }

    setSustainabilityLoan(sustainabilityLoan: boolean): void {
        if (!sustainabilityLoan) {
            this.sustainabilitySubject.next(null);
        }
        this.sustainabilityLoanSubject.next(sustainabilityLoan);
    }

    setSustainability(sustainability: Money): void {
        this.sustainabilitySubject.next(sustainability);
    }

    setRenovation(renovation: boolean): void {
        if (!renovation) {
            this.renovationAmountSubject.next(null);
        }
        this.renovationSubject.next(renovation);
    }

    setRenovationAmount(amount: Money): void {
        this.renovationAmountSubject.next(amount);
    }

    ngOnDestroy(): void {
        this.activeOptionSubject.unsubscribe();
        this.propertySubject.unsubscribe();
        this.propertyTypeSubject.unsubscribe();
        this.energyLabelSubject.unsubscribe();
        this.purchasePriceSubject.unsubscribe();
        this.sustainabilityLoanSubject.unsubscribe();
        this.sustainabilitySubject.unsubscribe();
        this.renovationSubject.unsubscribe();
        this.renovationAmountSubject.unsubscribe();
    }

    isActiveOption(question: PropertyOption): boolean {
        switch (question) {
            case PropertyOption.PROPERTY_TYPE:
            case PropertyOption.ENERGY_LABEL:
                return true;
            case PropertyOption.PURCHASE_PRICE:
            case PropertyOption.SUSTAINABILITY:
            case PropertyOption.RENOVATION:
                return this.propertyTypeSubject.value !== PropertyType.NONE;
            case PropertyOption.SUMMARY:
            default:
                return false;
        }
    }

    private update(): void {
        this.propertySubject.next(
            new PropertyBuilder()
                .withPropertyType(this.propertyTypeSubject.value)
                .withEnergyLabel(this.energyLabelSubject.value)
                .withPurchasePrice(this.purchasePriceSubject.value)
                .withSustainabilityLoan(this.sustainabilityLoanSubject.value)
                .withSustainability(this.sustainabilitySubject.value)
                .withRenovation(this.renovationSubject.value)
                .withRenovationAmount(this.renovationAmountSubject.value)
                .build()
        );
    }
}
